/* HERO WORD ROTATOR runtime (blocks/hero-word-rotator.liquid). The beat, the hand-off, the step
   and the letter events. The block's comment carries the design; this file only keeps its
   promises: one word changes per beat, the slots take turns, one accent on screen at a time, the
   previous accent is pure ink before the next word's first letter exists, nothing runs off-screen
   or in a hidden tab, and reduced motion (or the accessibility widget's "no animations") swaps in
   place with no letters and no events.

   HERO-6 (2026-09-07), LETTER BY LETTER. Every letter of an arriving word, at the instant it
   lands (opaque and sharp, the 55% point of its arrival), raises 'hero:letter' on the block's
   root, bubbling, with the accent, the colour it resolves to here, the LETTER's own box, its
   index and the word's letter count. When the last letter has landed the block still raises
   'hero:word' with the word's box, as HERO-5 did. Whoever owns the stage behind the sentence may
   answer either; this file knows nothing about what happens next, and on the instant path no
   event is raised at all.

   THE STEP (also HERO-6). The second row starts under the horizontal middle of the first row's
   word. The block measures the starter SLOT (as wide as its widest word, so the step never moves
   when the word changes) and writes the distance from the reading start to the slot's centre
   into --hwr-step on the root, in pixels along the inline axis; the CSS pushes the second row
   by that much with a logical margin. Measured after the fonts are in and again on resize,
   and clamped so the second row can never run past the block's edge. */
(function () {
  'use strict';
  var ACCENTS = ['gold', 'blue', 'ember'];
  var ACCENT_HEX = { gold: '#d7b36a', blue: '#4a9eff', ember: '#ff6700' };
  var DRAIN_MS = 320;
  /* Where along the letter's arrival it reads as landed: opaque and de-blurred at 55%. */
  var LAND_AT = 0.55;
  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function still() {
    return mq.matches || document.documentElement.classList.contains('a11y-no-animations');
  }

  function num(el, name, fallback) {
    var v = parseFloat(getComputedStyle(el).getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }

  function accentColor(root, accent) {
    return (getComputedStyle(root).getPropertyValue('--hwr-' + accent) || '').trim() || ACCENT_HEX[accent];
  }

  function raise(root, name, detail) {
    if (typeof CustomEvent !== 'function') return;
    try {
      root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    } catch (e) { /* an old engine without the constructor: the sentence still runs */ }
  }

  function announceLetter(root, cell, ch, accent, side, index, count) {
    if (!cell.classList.contains('is-on')) return;
    raise(root, 'hero:letter', {
      accent: accent, color: accentColor(root, accent), side: side,
      rect: ch.getBoundingClientRect(), index: index, count: count,
    });
  }

  function announceWord(root, cell, accent, side) {
    if (!cell.classList.contains('is-on')) return;
    raise(root, 'hero:word', {
      accent: accent, color: accentColor(root, accent), side: side, rect: cell.getBoundingClientRect(),
    });
  }

  /* The step: reading start of the first row to the centre of the starter slot, along the inline
     axis. The slot is measured, not the visible word, so the step holds while the words change.
     Clamped so the second row (whose width is also fixed by its widest word) stays inside the
     line. Written as a pixel length; the CSS applies it as a logical margin. */
  function measureStep(root) {
    var parts = root.querySelectorAll('.hwr__part');
    var slot = root.querySelector('.hwr__slot[data-slot="starter"]');
    var line = root.querySelector('.hwr__line');
    if (parts.length < 2 || !slot || !line) return;
    var rtl = getComputedStyle(root).direction === 'rtl';
    /* Measure at rest: with no step the rows sit at their natural widths. */
    root.style.setProperty('--hwr-step', '0px');
    var first = parts[0].getBoundingClientRect();
    var box = slot.getBoundingClientRect();
    if (!(first.width > 0 && box.width > 0)) return;
    var centre = box.left + box.width / 2;
    var step = rtl ? first.right - centre : centre - first.left;
    var room = line.clientWidth - parts[1].getBoundingClientRect().width;
    step = Math.max(0, Math.min(step, room));
    root.style.setProperty('--hwr-step', step.toFixed(1) + 'px');
  }

  function init(root) {
    if (root.__hwr) return;
    root.__hwr = true;
    var step = Math.max(1500, parseInt(root.getAttribute('data-step'), 10) || 4000);
    var slots = [];
    var slotEls = root.querySelectorAll('.hwr__slot');
    for (var i = 0; i < slotEls.length; i++) {
      var cells = slotEls[i].querySelectorAll('.hwr__cell');
      if (cells.length) slots.push({ cells: cells, i: 0, side: slotEls[i].getAttribute('data-slot') || '' });
    }

    /* The composition first: the step is layout, so it is measured on every path, motion or
       not, once the fonts are in and again whenever the width changes. */
    measureStep(root);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measureStep(root); });
    var resizeTimer = 0;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { measureStep(root); }, 120);
    });

    if (!slots.length) return;
    var movers = 0;
    for (var m = 0; m < slots.length; m++) if (slots[m].cells.length > 1) movers++;
    if (!movers) return;

    var turn = 0;
    var changes = 0;
    var accented = slots.length > 1 ? slots[1] : slots[0];
    var timer = 0;

    function tick() {
      var tries = 0;
      while (slots[turn].cells.length < 2 && tries < slots.length) { turn = (turn + 1) % slots.length; tries++; }
      var slot = slots[turn];
      if (slot.cells.length < 2) return;
      var cur = slot.cells[slot.i];
      var nextIndex = (slot.i + 1) % slot.cells.length;
      var next = slot.cells[nextIndex];
      changes++;
      var accent = ACCENTS[changes % ACCENTS.length];
      var instant = still();

      /* Drain the accent that is on screen now. It has DRAIN_MS to become ink; the incoming
         word does not exist before that. */
      accented.cells[accented.i].setAttribute('data-accent', 'ink');

      if (instant) {
        cur.classList.remove('is-on', 'is-in', 'is-out');
        next.setAttribute('data-accent', accent);
        next.classList.add('is-on');
      } else {
        var outChars = cur.querySelectorAll('.hwr__ch').length;
        var outMs = num(root, '--hwr-out-ms', 320) + outChars * num(root, '--hwr-out-stagger', 22) + 80;
        cur.classList.remove('is-on', 'is-in');
        cur.classList.add('is-out');
        setTimeout(function () { cur.classList.remove('is-out'); }, outMs);

        setTimeout(function () {
          next.setAttribute('data-accent', accent);
          next.classList.add('is-in', 'is-on');
          var chars = next.querySelectorAll('.hwr__ch');
          var count = chars.length;
          var glyphMs = num(root, '--hwr-in-ms', 520), stagger = num(root, '--hwr-in-stagger', 48);
          var inMs = glyphMs + count * stagger + 80;
          /* Each glyph lands at its own delay plus the landed fraction of its arrival; the word
             has landed when the last one has. */
          var land = function (n) {
            setTimeout(function () { announceLetter(root, next, chars[n], accent, slot.side, n, count); }, n * stagger + glyphMs * LAND_AT);
          };
          for (var n = 0; n < count; n++) land(n);
          var landMs = Math.max(0, count - 1) * stagger + glyphMs * LAND_AT;
          setTimeout(function () { announceWord(root, next, accent, slot.side); }, landMs);
          setTimeout(function () { next.classList.remove('is-in'); }, inMs);
        }, DRAIN_MS);
      }

      slot.i = nextIndex;
      accented = slot;
      turn = (turn + 1) % slots.length;
    }

    function run() {
      if (timer) return;
      timer = setInterval(tick, step);
    }
    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = 0;
    }

    var onScreen = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (onScreen && !document.hidden) run(); else stop();
      }, { threshold: 0 }).observe(root);
    } else {
      run();
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else if (onScreen) run();
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-hwr]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:load', boot);
})();
